import mixpanel from "mixpanel-browser";

import { env } from "@/lib/env";
import {
  JobPriority,
  NotificationAttribution,
  NotificationType,
} from "@/types";
import {
  MIXPANEL_CALL_REQUEST_ACTIONS,
  MIXPANEL_CALL_REQUEST_SOURCES,
  MIXPANEL_SCREENS,
  MIXPANEL_VANISHING_NOTIFICATION_DISMISSALS,
} from "./types";

type PropertyDict = Record<string, unknown>;

export const mixpanelUtils = {
  trackButtonClick: (
    buttonName: string,
    screenName: MIXPANEL_SCREENS,
    metadata?: PropertyDict,
  ) => {
    try {
      mixpanelUtils._track("Button Click", {
        button_name: buttonName,
        screen_name: screenName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel button click error:", error);
    }
  },

  trackViewClick: (
    tableName: string,
    viewName: string,
    metadata?: {
      value?: string | number;
    },
  ) => {
    try {
      mixpanelUtils._track("View Click", {
        table_name: tableName,
        view_name: viewName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel view click error:", error);
    }
  },

  trackScreenView: (
    screenName: MIXPANEL_SCREENS,
    metadata?: { subScreenName: string },
  ) => {
    try {
      mixpanelUtils._track("Screen View", {
        screen_name: screenName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel screen view error:", error);
    }
  },

  trackTabView: (screenName: MIXPANEL_SCREENS, tabName: string) => {
    try {
      mixpanelUtils._track("Tab View", {
        screen_name: screenName,
        tab_name: tabName,
      });
    } catch (error) {
      console.error("Mixpanel tab view error:", error);
    }
  },

  trackTabTime: (
    screenName: MIXPANEL_SCREENS,
    tabName: string,
    durationSeconds: number,
  ) => {
    try {
      mixpanelUtils._track("Tab Time", {
        duration_seconds: durationSeconds,
        screen_name: screenName,
        tab_name: tabName,
      });
    } catch (error) {
      console.error("Mixpanel tab time error:", error);
    }
  },

  trackSearchResultsLoaded: (
    screenName: MIXPANEL_SCREENS,
    metadata: PropertyDict,
  ) => {
    try {
      mixpanelUtils._track("Search Results Loaded", {
        screen_name: screenName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel search results error:", error);
    }
  },

  trackFilterAndSortClick: (
    screenName: MIXPANEL_SCREENS,
    sortFilterName: string,
    metadata?: PropertyDict,
  ) => {
    try {
      mixpanelUtils._track("Filter And Sort Click", {
        screen_name: screenName,
        sort_filter_name: sortFilterName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel filter and sort click error:", error);
    }
  },

  trackFilterAndSortOptionSelected: (
    screenName: MIXPANEL_SCREENS,
    sortFilterName: string,
    optionSelected: string | boolean | unknown[],
    metadata?: PropertyDict,
  ) => {
    try {
      mixpanelUtils._track("Filter And Sort Option Selected", {
        screen_name: screenName,
        sort_filter_name: sortFilterName,
        option_selected: optionSelected,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel filter and sort option selected error:", error);
    }
  },

  trackKeyboardShortcutUsed: (
    shortcutName: string,
    screenName: MIXPANEL_SCREENS,
  ) => {
    try {
      mixpanelUtils._track("Keyboard Shortcut Used", {
        shortcut_name: shortcutName,
        screen_name: screenName,
      });
    } catch (error) {
      console.error("Mixpanel keyboard shortcut used error:", error);
    }
  },

  trackNavClick: (
    destination: MIXPANEL_SCREENS,
    fromScreen: MIXPANEL_SCREENS,
  ) => {
    try {
      mixpanelUtils._track("Sidebar Nav", {
        destination,
        from_screen: fromScreen,
      });
    } catch (error) {
      console.error("Mixpanel sidebar nav error:", error);
    }
  },

  trackRowClick: (
    rowType: string,
    screenName: MIXPANEL_SCREENS,
    metadata?: PropertyDict,
  ) => {
    try {
      mixpanelUtils._track("Row Click", {
        row_type: rowType,
        screen_name: screenName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel row click error:", error);
    }
  },

  trackFormSubmission: (
    formName: string,
    screenName: MIXPANEL_SCREENS,
    metadata?: PropertyDict,
  ) => {
    try {
      mixpanelUtils._track("Form Submission", {
        form_name: formName,
        screen_name: screenName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel form submission error:", error);
    }
  },

  trackHover: (elementName: string, screenName: MIXPANEL_SCREENS) => {
    try {
      mixpanelUtils._track("Hover", {
        element_name: elementName,
        screen_name: screenName,
      });
    } catch (error) {
      console.error("Mixpanel hover error:", error);
    }
  },

  trackDragDrop: (
    actionName: string,
    screenName: MIXPANEL_SCREENS,
    metadata?: PropertyDict,
  ) => {
    try {
      mixpanelUtils._track("Drag Drop", {
        action_name: actionName,
        screen_name: screenName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel drag drop error:", error);
    }
  },

  trackInlineEdit: (
    fieldName: string,
    screenName: MIXPANEL_SCREENS,
    metadata?: PropertyDict,
  ) => {
    try {
      mixpanelUtils._track("Inline Edit", {
        field_name: fieldName,
        screen_name: screenName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel inline edit error:", error);
    }
  },

  // Fired when a link carrying ?mixpanel-attr= is opened (e.g. candidacy
  // links in Slack DMs), attributing the visit to its source.
  trackLinkAttribution: (
    attribution: NotificationAttribution,
    screenName: MIXPANEL_SCREENS,
    metadata?: PropertyDict,
  ) => {
    try {
      mixpanelUtils._track("Link Attribution", {
        attribution,
        screen_name: screenName,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel link attribution error:", error);
    }
  },

  // Fired when a popup that vanishes on its own leaves the pile — either the
  // staffer closed it first, or its 15 seconds ran out.
  trackVanishingNotificationDismissal: (metadata: {
    notification_type: NotificationType;
    dismissal: MIXPANEL_VANISHING_NOTIFICATION_DISMISSALS;
    seconds_since_created: number;
  }) => {
    try {
      mixpanelUtils._track("Vanishing Notification Dismissal", {
        screen_name: MIXPANEL_SCREENS.NOTIFICATIONS,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel vanishing notification dismissal error:", error);
    }
  },

  // Fired when a staffer drives their applied inbox to 0 from dashboard.
  trackAppliedInboxCleared: (metadata: { previous_count: number }) => {
    try {
      mixpanelUtils._track("Applied Inbox Cleared", {
        screen_name: MIXPANEL_SCREENS.DASHBOARD,
        ...metadata,
      });
    } catch (error) {
      console.error("Mixpanel applied inbox cleared error:", error);
    }
  },

  // One event per step of the two-minute call-request prompt, so viewed vs
  // accepted vs cancelled is a funnel rather than three separate charts.
  trackCallRequestPrompt: (
    action: MIXPANEL_CALL_REQUEST_ACTIONS,
    metadata: {
      agentic_score: number | null;
      candidacy_id: string;
      job_priority: JobPriority | null;
      seconds_remaining: number;
      source: MIXPANEL_CALL_REQUEST_SOURCES;
    },
  ) => {
    try {
      mixpanelUtils._track("Call Request Prompt", { action, ...metadata });
    } catch (error) {
      console.error("Mixpanel call request prompt error:", error);
    }
  },

  _track: (eventName: string, properties: PropertyDict) => {
    if (!env.getIsProduction()) return;
    mixpanel.track(eventName, properties);
  },
};

export const getScreenFromPathname = (pathname: string): MIXPANEL_SCREENS => {
  // Tools subscreens (check before generic /tools fallback).
  if (pathname.startsWith("/tools/campaigns"))
    return MIXPANEL_SCREENS.CAMPAIGNS;
  if (pathname.startsWith("/tools/demand")) return MIXPANEL_SCREENS.DEMAND;
  if (pathname.startsWith("/tools/demo-configs"))
    return MIXPANEL_SCREENS.DEMO_CONFIGS;
  if (pathname.startsWith("/tools/job-tracker"))
    return MIXPANEL_SCREENS.JOB_TRACKER;
  if (pathname.startsWith("/tools/metrics")) return MIXPANEL_SCREENS.METRICS;
  if (pathname.startsWith("/tools/sadie-dashboard"))
    return MIXPANEL_SCREENS.SADIE_DASHBOARD;
  if (pathname.startsWith("/tools/sadie-simulator"))
    return MIXPANEL_SCREENS.SADIE_SIMULATOR;
  if (pathname.startsWith("/tools/sadie-voice-calls"))
    return MIXPANEL_SCREENS.SADIE_VOICE_CALLS;
  if (pathname.startsWith("/tools/orphaned-workable-jobs"))
    return MIXPANEL_SCREENS.ORPHANED_WORKABLE_JOBS;
  if (pathname.startsWith("/tools/signup-variants"))
    return MIXPANEL_SCREENS.SIGNUP_VARIANTS;
  if (pathname.startsWith("/tools/users")) return MIXPANEL_SCREENS.USERS;

  // Top-level screens.
  if (pathname === "/placement-tracker")
    return MIXPANEL_SCREENS.PLACEMENT_TRACKER;
  if (pathname === "/my-aviary") return MIXPANEL_SCREENS.MY_AVIARY;
  if (pathname === "/leaderboard") return MIXPANEL_SCREENS.LEADERBOARD;
  if (pathname === "/earnings") return MIXPANEL_SCREENS.EARNINGS;
  if (pathname === "/job-health") return MIXPANEL_SCREENS.JOB_HEALH;
  if (pathname.startsWith("/notifications"))
    return MIXPANEL_SCREENS.NOTIFICATIONS;
  if (pathname === "/motivation") return MIXPANEL_SCREENS.MOTIVATION;
  if (pathname === "/sign-in") return MIXPANEL_SCREENS.SIGN_IN;
  if (pathname === "/post-sign-in") return MIXPANEL_SCREENS.POST_SIGN_IN;

  // Companies area — from these screens we can navigate to other screen so must track.
  if (/^\/companies\/[^/]+\/jobs\/create$/.test(pathname))
    return MIXPANEL_SCREENS.JOB_CREATE;
  if (/^\/companies\/[^/]+\/jobs\/[^/]+/.test(pathname))
    return MIXPANEL_SCREENS.JOB;
  if (/^\/companies\/[^/]+/.test(pathname)) return MIXPANEL_SCREENS.COMPANY;
  if (pathname === "/companies" || pathname === "/")
    return MIXPANEL_SCREENS.COMPANIES;

  return MIXPANEL_SCREENS.CATCH_ALL_PAGE;
};
